/**
 * UnitLines (TECH-READ-01, Seth 2026-09-25: "Serial numbers, equipment, etc
 * are nice and legible") — one unit on the phone, the same everywhere: what it
 * is, the model, the serial. The model is the headline (17px bold); the serial
 * gets its own line as a 16px copy chip — the two codes a tech reads off a data
 * tag or into a parts counter, tap or hold to copy. Styles: .m-unit-* in
 * index.css (tokens, so the dark theme follows).
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { CopyCode } from "@/components/Copyable";
import { equipStyle } from "@/lib/equipmentIcons";

export function UnitLines({ kind, category, brand, model, serial, meta, muted, serialLabel = "S/N", serialFallback, children }: {
  /** What it is — "Furnace", "Condenser". */
  kind?: string | null;
  /** 34i83: the classified category — puts the same icon the office sees (fan = condenser,
   *  snowflake = coil, flame = furnace …) in front of the kind line. */
  category?: string | null;
  brand?: string | null;
  model?: string | null;
  serial?: string | null;
  /** The small line under — "Basement · installed 2019". */
  meta?: ReactNode;
  /** A replaced / old unit. */
  muted?: boolean;
  serialLabel?: string;
  /** Shown in place of the serial when there isn't one ("No serial logged yet"). */
  serialFallback?: ReactNode;
  /** Status lines under the unit (confirmed, changed on site…). */
  children?: ReactNode;
}) {
  return (
    <div className={cn("min-w-0", muted && "opacity-60")}>
      {kind && (
        <div className="m-unit-kind flex items-center gap-1.5">
          {category && (() => { const { Icon, ink } = equipStyle(category); return <Icon className={cn("h-4 w-4 shrink-0", muted ? "text-slate-400" : ink)} strokeWidth={2} />; })()}
          <span>{kind}</span>
        </div>
      )}
      <div className="m-unit-model">
        {brand && <span className="mr-1.5">{brand}</span>}
        {model
          ? <CopyCode value={model} label="Copy model" variant="text" mono={false} className="text-[17px] font-bold" />
          : !brand && <span>{kind || "Unit"}</span>}
      </div>
      {serial ? (
        <div className="m-unit-serial mt-1">
          <span>{serialLabel}</span>
          <CopyCode value={serial} label="Copy serial" size="lg" className="text-[16px]" />
        </div>
      ) : serialFallback ? (
        <div className="m-unit-meta mt-0.5">{serialFallback}</div>
      ) : null}
      {meta && <div className="m-unit-meta mt-1">{meta}</div>}
      {children}
    </div>
  );
}
